import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { generateQrMatrix } from '../utils/qr';

/**
 * Dependency-free QR renderer. Encodes `value` into a QR matrix (see utils/qr)
 * and paints it to a canvas. Used to render the otpauth:// setup URI returned
 * by Amplify's setUpTOTP so users can scan it — no external QR package.
 */
const QrCanvas = ({ value, size }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !value) return;

    const ctx = canvas.getContext('2d');
    let matrix;
    try {
      matrix = generateQrMatrix(value);
    } catch (err) {
      // If the value is too large for supported versions, clear and bail.
      ctx.clearRect(0, 0, size, size);
      console.warn('QR generation failed:', err?.message);
      return;
    }

    const count = matrix.length;
    const quiet = 4; // quiet-zone modules on each side
    const total = count + quiet * 2;
    const scale = Math.floor(size / total) || 1;
    const drawSize = scale * total;

    canvas.width = drawSize;
    canvas.height = drawSize;

    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, drawSize, drawSize);

    ctx.fillStyle = '#002147'; // brand dark
    for (let r = 0; r < count; r += 1) {
      for (let c = 0; c < count; c += 1) {
        if (matrix[r][c]) {
          ctx.fillRect((c + quiet) * scale, (r + quiet) * scale, scale, scale);
        }
      }
    }
  }, [value, size]);

  return (
    <canvas
      ref={canvasRef}
      style={{ width: '100%', height: '100%', imageRendering: 'pixelated' }}
      aria-label="Authenticator setup QR code"
    />
  );
};

QrCanvas.propTypes = {
  value: PropTypes.string,
  size: PropTypes.number,
};

QrCanvas.defaultProps = {
  value: '',
  size: 148,
};

export default QrCanvas;
